/**
 * MorningBrief — Phase 7
 *
 * Renders two pieces:
 *   - briefPanel(): a card pinned at the top of the left rail when
 *     today's brief is <8h old. Shows the date, the cost the brief
 *     consumed, a quick anomaly badge, and "open" / "rerun" actions.
 *   - briefDrawer(): the global drawer that displays the full brief
 *     markdown when the operator clicks open. Mounted once in App.ts.
 */
import { html, render, type TemplateResult } from "lit-html";
import { store } from "../data/store";
import { api } from "../data/api";
import type { MorningBriefLatest } from "../data/types";
import { renderExplainButton } from "./ExplainButton";

// =============================================================
// Pinned card (left rail)
// =============================================================
const FRESH_WINDOW_MS = 8 * 3600_000;     // <8h old = pin

export function renderMorningBriefPanel(): TemplateResult {
  const e = store.get<MorningBriefLatest>("morningBrief");
  if (!e.data && e.loading) return panelLoading();
  if (!e.data || (e.data as any).error) return panelEmpty();

  const ageMs = Date.parse(e.data.generated_at)
    ? Date.now() - Date.parse(e.data.generated_at)
    : Infinity;
  if (ageMs > FRESH_WINDOW_MS) return panelEmpty();   // stale — show generator

  const ageStr = humanAgo(ageMs);
  const briefSnippet = (e.data.brief ?? "").split("\n")[0]?.replace(/^\W+/, "").slice(0, 80) ?? "";

  return html`
    <section class="panel panel--hero mbrief-pinned"
             @click=${() => openDrawer(e.data!)}>
      <header class="mbrief-pinned__head">
        <span class="mbrief-pinned__icon">☀</span>
        <span class="mbrief-pinned__title">Morning brief</span>
        <span class="mbrief-pinned__spacer"></span>
        <span class="mbrief-pinned__age">${ageStr}</span>
      </header>
      <div class="mbrief-pinned__body">
        <div class="mbrief-pinned__date">${e.data.date}</div>
        ${briefSnippet ? html`
          <div class="mbrief-pinned__snippet">${briefSnippet}…</div>
        ` : ""}
      </div>
      <footer class="mbrief-pinned__foot">
        <span class="mbrief-pinned__meta">
          ${e.data.n_bets_yesterday}b · ${e.data.n_anomalies_24h}a · $${e.data.cost_usd.toFixed(3)}
        </span>
        <span class="mbrief-pinned__cta">read →</span>
      </footer>
    </section>
  `;
}

function panelLoading(): TemplateResult {
  return html`
    <section class="panel mbrief-pinned mbrief-pinned--loading">
      <div class="skeleton" style="height:14px;width:50%;margin-bottom:8px;"></div>
      <div class="skeleton" style="height:12px;width:80%;"></div>
    </section>
  `;
}

// Empty state — no brief or it's stale (>8h). Lets the operator
// generate one on demand instead of waiting for the next 08:00 UTC
// cron tick. Cost: ~$0.005-0.01 per fire.
let generating = false;
let generateError: Error | null = null;

function panelEmpty(): TemplateResult {
  return html`
    <section class="panel panel--hero mbrief-empty">
      <header class="mbrief-empty__head">
        <span class="mbrief-pinned__icon">☀</span>
        <span class="mbrief-pinned__title">Morning brief</span>
      </header>
      ${generateError ? html`
        <div class="mbrief-empty__err">${generateError.message}</div>
      ` : ""}
      <p class="mbrief-empty__msg">
        ${generating
          ? "Generating brief — this takes 3-6s…"
          : "No fresh brief. Cron fires daily at 08:00 UTC (3 AM ET). You can also generate one now."}
      </p>
      <button class="mbrief-empty__btn"
              ?disabled=${generating}
              @click=${() => generateNow()}>
        ${generating ? "…running" : "↻ Generate brief now"}
      </button>
      <span class="mbrief-empty__cost">~$0.005-0.01 per fire</span>
    </section>
  `;
}

async function generateNow(): Promise<void> {
  if (generating) return;
  generating = true;
  generateError = null;
  // Force a re-render of the LeftRail so the button shows the running state
  store.refetch("morningBrief");
  try {
    await api.morningBriefRerun();
    store.refetch("morningBrief");          // pull the now-existing brief
  } catch (e) {
    generateError = e instanceof Error ? e : new Error(String(e));
  } finally {
    generating = false;
    store.refetch("morningBrief");
  }
}

// =============================================================
// Drawer (global, mounted once in App.ts)
// =============================================================
interface DrawerState {
  open:       boolean;
  brief:      MorningBriefLatest | null;
  rerunning:  boolean;
  rerunError: Error | null;
  archive:    string[] | null;
  paint:      () => void;
}
const drawerState: DrawerState = {
  open:       false,
  brief:      null,
  rerunning:  false,
  rerunError: null,
  archive:    null,
  paint:      () => {},
};

export function renderMorningBriefDrawer(root: HTMLElement): void {
  drawerState.paint = () => render(drawerTemplate(), root);
  drawerState.paint();
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && drawerState.open) {
      e.preventDefault();
      closeDrawer();
    }
  });
}

function drawerTemplate(): TemplateResult {
  if (!drawerState.open || !drawerState.brief) return html``;
  const b = drawerState.brief;

  return html`
    <div class="mbrief-backdrop"
         @click=${(e: MouseEvent) => {
           if (e.target === e.currentTarget) closeDrawer();
         }}>
      <aside class="mbrief-drawer glass">
        <header class="mbrief-drawer__head">
          <span class="mbrief-drawer__icon">☀</span>
          <h2 class="mbrief-drawer__title">
            Morning brief · <span class="mbrief-drawer__date">${b.date}</span>
          </h2>
          <span class="mbrief-drawer__spacer"></span>
          <button class="mbrief-drawer__btn"
                  ?disabled=${drawerState.rerunning}
                  @click=${rerun}
                  title="Re-run the brief now (forces fresh Claude call)">
            ${drawerState.rerunning ? "…running" : "↻ rerun"}
          </button>
          <button class="mbrief-drawer__close"
                  @click=${closeDrawer}
                  title="Close (Esc)">×</button>
        </header>

        <div class="mbrief-drawer__body">
          ${drawerState.rerunError ? html`
            <div class="mbrief-drawer__error">
              <strong>rerun error</strong> ${drawerState.rerunError.message}
            </div>
          ` : ""}

          <div class="mbrief-drawer__meta">
            <span class="mbrief-drawer__meta-pill">
              ${b.n_bets_yesterday} bet${b.n_bets_yesterday === 1 ? "" : "s"} yesterday
            </span>
            <span class="mbrief-drawer__meta-pill">
              ${b.n_anomalies_24h} anomalies (24h)
            </span>
            <span class="mbrief-drawer__meta-pill">
              $${b.cost_usd.toFixed(4)}
            </span>
            <span class="mbrief-drawer__meta-pill">
              ${b.input_tokens + b.output_tokens} tok
            </span>
            <span class="mbrief-drawer__spacer"></span>
            ${renderExplainButton({
              label: "Re-narrate this brief",
              context: () => `Re-summarize the morning brief below in your own words. Be more concise. Original brief follows:\n\n${b.brief ?? ""}`,
            })}
          </div>

          <article class="mbrief-drawer__brief">
            ${renderBriefMarkdown(b.brief ?? "")}
          </article>

          ${drawerState.archive && drawerState.archive.length > 0 ? html`
            <section class="mbrief-drawer__archive">
              <h4>Archive</h4>
              <ol>
                ${drawerState.archive.slice(0, 14).map((d) => html`
                  <li>
                    <button class="mbrief-archive-row"
                            @click=${() => loadArchiveDate(d)}>
                      <span>${d}</span>
                      <span class="mbrief-archive-row__hint">read →</span>
                    </button>
                  </li>
                `)}
              </ol>
            </section>
          ` : ""}
        </div>
      </aside>
    </div>
  `;
}

// =============================================================
// Drawer actions
// =============================================================
async function openDrawer(b: MorningBriefLatest): Promise<void> {
  drawerState.open = true;
  drawerState.brief = b;
  drawerState.rerunError = null;
  drawerState.paint();
  // Lazy-load archive on first open
  if (drawerState.archive == null) {
    try {
      const r = await fetch("/api/morning-brief-archive", { credentials: "include" });
      if (r.ok) {
        const j = await r.json() as { dates: string[] };
        drawerState.archive = j.dates ?? [];
        drawerState.paint();
      }
    } catch { /* noop */ }
  }
}

function closeDrawer(): void {
  drawerState.open = false;
  drawerState.paint();
}

async function rerun(): Promise<void> {
  if (drawerState.rerunning) return;
  drawerState.rerunning = true;
  drawerState.rerunError = null;
  drawerState.paint();
  try {
    const result = await api.morningBriefRerun();
    // morningBriefRerun returns { date, brief } shape — merge with existing meta
    drawerState.brief = {
      ...drawerState.brief!,
      date:         result.date ?? drawerState.brief!.date,
      brief:        (result as any).brief ?? drawerState.brief?.brief,
      generated_at: new Date().toISOString(),
    };
    // Force store refetch so the pinned card updates too
    store.refetch("morningBrief");
  } catch (e) {
    drawerState.rerunError = e instanceof Error ? e : new Error(String(e));
  } finally {
    drawerState.rerunning = false;
    drawerState.paint();
  }
}

async function loadArchiveDate(date: string): Promise<void> {
  try {
    const r = await fetch(`/api/morning-brief?date=${encodeURIComponent(date)}`,
                          { credentials: "include" });
    if (!r.ok) return;
    const j = await r.json() as { date: string; brief: string };
    drawerState.brief = {
      ...(drawerState.brief ?? {} as any),
      date: j.date,
      brief: j.brief,
      generated_at: drawerState.brief?.generated_at ?? "",
    };
    drawerState.paint();
  } catch { /* noop */ }
}

// =============================================================
// Helpers
// =============================================================
function humanAgo(ms: number): string {
  const m = Math.floor(ms / 60_000);
  if (m < 1)  return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// Renders a 5-bullet brief that uses **Tag:** then prose. Each bullet
// becomes its own row with a colored tag pill.
function renderBriefMarkdown(text: string): TemplateResult {
  const lines = text.split("\n").filter((l) => l.trim());
  const tagRE = /^\s*[-*•]?\s*\*\*([A-Z][\w \-]+):\*\*\s*(.+)$/i;

  const out: TemplateResult[] = [];
  for (const line of lines) {
    const m = line.match(tagRE);
    if (m) {
      const tag = m[1]!.toLowerCase().split(" ")[0]!;
      const body = m[2]!;
      const tagColor = TAG_COLORS[tag] ?? "var(--text-secondary)";
      out.push(html`
        <div class="mbrief-bullet">
          <span class="mbrief-bullet__tag" style="color:${tagColor};border-color:${tagColor};">
            ${m[1]}
          </span>
          <span class="mbrief-bullet__body">${renderInlineFormatting(body)}</span>
        </div>
      `);
    } else if (line.startsWith("—")) {
      // Trailing footer line
      out.push(html`<div class="mbrief-footer">${line}</div>`);
    } else {
      out.push(html`<div class="mbrief-line">${renderInlineFormatting(line)}</div>`);
    }
  }
  return html`${out}`;
}

const TAG_COLORS: Record<string, string> = {
  calibration: "var(--accent-amber)",
  anomalies:   "var(--accent-red-light)",
  cost:        "var(--accent-blue-light)",
  top:         "var(--accent-purple-light)",     // matches "Top miss"
  recommended: "var(--accent-green-light)",
};

function renderInlineFormatting(line: string): TemplateResult[] {
  const segs: TemplateResult[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(line)) !== null) {
    if (m.index > last) segs.push(html`${line.slice(last, m.index)}`);
    const tok = m[0];
    if (tok.startsWith("**")) {
      segs.push(html`<strong>${tok.slice(2, -2)}</strong>`);
    } else {
      segs.push(html`<code>${tok.slice(1, -1)}</code>`);
    }
    last = re.lastIndex;
  }
  if (last < line.length) segs.push(html`${line.slice(last)}`);
  return segs;
}
